import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { catalogueEntryOf } from "../../catalogue/types";
import { useCatalogue, useLabels, useLayers, useMapCore, useMapStore, useReducedMotion } from "../hooks";

export type MiniMapProps = { zoomOffset?: number; className?: string };

export const miniMapZoomOffset = 5;

type MiniState = { map: L.Map; viewport: L.Rectangle; tiles: L.TileLayer | null; tileUrl: string | null };

export function MiniMap({ zoomOffset = miniMapZoomOffset, className }: MiniMapProps) {
  const core = useMapCore();
  const labels = useLabels();
  const view = useMapStore(a => a.view);
  const catalogue = useCatalogue();
  const { list } = useLayers();
  const reducedMotion = useReducedMotion();
  const canvasRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<MiniState | null>(null);
  const [initialView] = useState(view);
  const [ready, setReady] = useState(false);
  const selectedBase = list.find(a => a.root === "maps" && a.selected && a.kind === "tile") ?? null;
  const baseEntry = selectedBase === null ? null : catalogueEntryOf(catalogue, selectedBase);
  const tileUrl = baseEntry?.tile?.url ?? null;
  const subdomains = baseEntry?.tile?.subdomains ?? "abc";

  useEffect(() => {
    if (typeof window === "undefined" || canvasRef.current === null) return;

    const map = L.map(canvasRef.current, {
      attributionControl: false,
      zoomControl: false,
      dragging: false,
      touchZoom: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      zoomSnap: 0.25,
      minZoom: 0,
      maxZoom: 19
    });
    const viewport = L.rectangle(L.latLngBounds(initialView.center, initialView.center), { weight: 2, fillOpacity: 0.12, interactive: false, className: "fxmap-minimap-viewport" }).addTo(map);

    map.setView(initialView.center, Math.max(0, initialView.zoom - zoomOffset), { animate: false });
    map.on("click", (event: L.LeafletMouseEvent) => core.view.panTo([event.latlng.lat, event.latlng.lng]));
    L.DomEvent.disableClickPropagation(map.getContainer());
    L.DomEvent.disableScrollPropagation(map.getContainer());
    stateRef.current = { map, viewport, tiles: null, tileUrl: null };
    setReady(true);

    const timer = setTimeout(() => map.invalidateSize(), 0);

    return () => {
      clearTimeout(timer);
      map.remove();
      stateRef.current = null;
      setReady(false);
    };
  }, [core, zoomOffset, initialView]);

  useEffect(() => {
    const state = stateRef.current;

    if (!ready || state === null) return;

    state.map.setView(view.center, Math.max(state.map.getMinZoom(), view.zoom - zoomOffset), { animate: !reducedMotion });

    if (view.bounds !== null) state.viewport.setBounds(view.bounds);
  }, [ready, view, zoomOffset, reducedMotion]);

  useEffect(() => {
    const state = stateRef.current;

    if (!ready || state === null || state.tileUrl === tileUrl) return;

    if (state.tiles !== null) state.map.removeLayer(state.tiles);

    state.tiles = tileUrl === null ? null : L.tileLayer(tileUrl, { subdomains, maxZoom: 19 }).addTo(state.map);
    state.tileUrl = tileUrl;
  }, [ready, tileUrl, subdomains]);

  return (
    <div className={["fxmap-minimap", "fxmap-card", className ?? ""].filter(Boolean).join(" ")} data-fxmap="minimap" role="img" aria-label={labels.miniMap}>
      <div ref={canvasRef} className="fxmap-minimap-canvas" />
    </div>
  );
}
